import { observer } from 'mobx-react';
import React from 'react';

import { StringKeyMap } from '../../../adl-gen/common';
import { CompetencySkillTrainingOffering } from '../../../adl-gen/totalinteraction/futurefit/db';
import { RecommendationResult } from '../../../adl-gen/totalinteraction/futurefit/frontend';
import { i18next } from '../../../internationalization/i18next';
import { CategoryOption, LearningType } from '../../../stores/quiz-results-store';
// import { SelectOption } from "../../widgets/select/select";
import { SectionAccordion } from '../section-accordion/section-accordion';
import { Tile } from '../tile/tile';

const styles = require('./tile-accordions.css');

export interface QuizTile {
  title: string;
  subtitle?: string;
  imageUrl: string;
  progress: number;
  total: number;
  id: string;
  enabled: boolean;
  onClick(): void;
  index: number;
}

export interface RecommendationTile {
  /// Offering title
  title: string;
  /// Tile image
  imageUrl: string;
  /// This ends up in the Pill. TrainingOffering Category or Competency/Skill?
  tag: string;
  /// This is set to the ExecuteUrl string
  url: string;
  /// Set to DbKey<TrainingOffering>
  key: string;
  /// Actual result as captured from UserTrainingOffering
  result: RecommendationResult;
  enabled: boolean;
  /// We've had notification from the LP that the offering has been completed
  completed: boolean;
  /// Launch
  onClick(): void;
}

export interface ReportTile {
  title: string;
  imageUrl: string;
  tag: string;
  id: string;
  // priority: number;
  enabled: boolean;
  onClick(): Promise<void>;
}

type TileAccordionsProps =
  | {
      kind: 'progress-manager';
      quizTiles: QuizTile[];
    }
  | {
      kind: 'progress-participant';
      assessmentType?: 'quiz-assessment' | 'self-assessment';
      quizTiles: QuizTile[];
    }
  | {
      kind: 'recommendation-category';
      selectedPriorities?: LearningType[];
      recommendationTiles: RecommendationTile[];
      allAvailableTiles?: RecommendationTile[];
    }
  | {
      kind: 'recommendation-external' | 'recommendation-external-controlled';
      /// SkillTrainingOfferings or CompetencyTrainingOfferings, keyed on skillId or competencyId
      categoryMap: StringKeyMap<string, CompetencySkillTrainingOffering[]>;
      categories: CategoryOption[];
      recommendationTiles: RecommendationTile[];
      selectedPriorities?: LearningType[];
      allAvailableTiles?: RecommendationTile[];
    }
  | {
      kind: 'report';
      reportTiles: ReportTile[];
    };

@observer
export class TileAccordions extends React.Component<TileAccordionsProps> {
  render() {
    switch (this.props.kind) {
      case 'progress-participant': {
        /// Assume default 'quiz-assessment'
        let openTitle = i18next.t('tile-accordions.progress.participant.quiz-in-progress', 'Assessments to Complete');
        let completedTitle = i18next.t('tile-accordions.progress.participant.quiz-completed', 'Completed assessments');
        if (this.props.assessmentType === 'self-assessment') {
          openTitle = i18next.t(
            'tile-accordions.progress.participant.self-in-progress',
            'Self-Assessments to Complete'
          );
          completedTitle = i18next.t(
            'tile-accordions.progress.participant.self-completed',
            'Completed self-assessments'
          );
        }
        return (
          <>
            {this.renderUserQuizCategory(openTitle, this.props.quizTiles.filter(qT => !this.userQuizIsCompleted(qT)))}
            {this.renderUserQuizCategory(
              completedTitle,
              this.props.quizTiles.filter(uQ => this.userQuizIsCompleted(uQ))
            )}
          </>
        );
      }

      case 'progress-manager': {
        return (
          <>
            {this.renderUserQuizCategory(
              i18next.t('tile-accordions.progress.manager', "My Team's Assessments"),
              this.props.quizTiles.sort((a, b) => a.title.localeCompare(b.title))
            )}
            {/* {this.renderUserQuizCategory(i18next.t("tile-accordions.progress.completed","Completed assessments"), this.props.quizTiles.filter((uQ) => this.userQuizIsCompleted(uQ)))} */}
          </>
        );
      }

      case 'recommendation-category': {
        const { allAvailableTiles: allAvailableRecs, recommendationTiles, selectedPriorities } = this.props;
        const completedRecs = recommendationTiles.filter(tile => tile.completed);
        const notCompletedRecs = recommendationTiles.filter(tile => !tile.completed);
        const someCompletedRecs = completedRecs.length !== 0;
        /// Add all priorities if empty *except* all-learning
        const filteredLearningTypes: LearningType[] = selectedPriorities || ['recommendations'];
        return (
          <>
            {filteredLearningTypes.includes('recommendations') &&
              this.renderRecommendationInternalCategory(
                i18next.t('tile-accordions.recommendations.highly-recommended', 'Highly recommended'),
                notCompletedRecs.slice(0, 6)
              )}
            {filteredLearningTypes.includes('recommendations') &&
              this.renderRecommendationInternalCategory(
                i18next.t('tile-accordions.recommendations.recommended', 'Recommended'),
                notCompletedRecs.slice(6, notCompletedRecs.length),
                !someCompletedRecs
              )}
            {filteredLearningTypes.includes('recommendations') &&
              someCompletedRecs &&
              this.renderRecommendationInternalCategory(
                i18next.t('tile-accordions.recommendations.completed', 'Take Again'),
                completedRecs
              )}
            {filteredLearningTypes.includes('all-learning') &&
              allAvailableRecs &&
              this.renderRecommendationInternalCategory(
                i18next.t('tile-accordions.recommendations.all-learning', 'All Learning'),
                allAvailableRecs
              )}
          </>
        );
      }

      case 'recommendation-external': {
        const { categoryMap, recommendationTiles } = this.props;
        const categories = this.props.categories
          .slice()
          .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));
        return (
          <>
            {categories.map(category => {
              const recs = recommendationTiles.filter(tile => {
                if (tile.result.kind === 'roleScore') {
                  return tile.result.value.skillOrCompetency === category.value;
                } else {
                  return categoryMap[category.value].map(cs => cs.value.trainingOfferingId).includes(tile.key);
                }
              });

              /// Don't return an accordion if there's no content
              if (recs.length === 0) {
                return null;
              }

              return this.renderRecommendationExternalCategory(category.label, category.value as string, recs);
            })}

            {/* {Object.keys(categoryMap).map(k => {
            const csRecs = recommendationTiles.filter(tile => categoryMap[k].map(cs => cs.value.trainingOfferingId).includes(tile.key));
  
            /// Don't return an accordion if there's no content
            if (csRecs.length === 0) { return null; }
  
            let title = k;
            const option = categories.find(c => c.value === k);
            if (option !== undefined) {
               title = option.label; 
              /// Handle the problem of a mismatch between tags set in recommendations.tsx (see deprecation warning on getAssociatedCompetencyOrSkill)
              /// We could just overwrite with the category name, but let's see if this works first (=> consistency of data)
              setTags(csRecs, categoryMap, categories, option.value as string);
            }
            return this.renderRecommendationExternalCategory(title, k, csRecs);
          })} */}
          </>
        );
      }

      case 'recommendation-external-controlled': {
        const { allAvailableTiles, categoryMap, recommendationTiles, selectedPriorities } = this.props;
        const categoryList = this.props.categories
          .slice()
          .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));
        /// This list will contain 'duplicates' - competency names with multiple entries, for both Competencies and Skills. While
        /// we only display those with content, this is ok. However, if we now display an empty category, this can show up twice,
        /// or more - eg. if we have one competency with 10 skills and they're all empty and the gap < 1.0 we'll display 11
        /// identically empty accordions. Not ideal
        const categories: StringKeyMap<string, RecommendationTile[]> = {};

        /// Sort out priorities - only all-learning is used
        const filteredPriorities: LearningType[] = selectedPriorities || [];
        if (filteredPriorities.length > 0 && filteredPriorities.includes('all-learning')) {
          categories['All Learning'] = allAvailableTiles || [];
        } else {
          for (const c of categoryList) {
            /// (1) Show no accordion at all if gap > 100%
            if (c.gap !== undefined && c.gap >= 1.0) {
              continue;
            }

            if (!Object.keys(categories).includes(c.label)) {
              categories[c.label] = [];
            }

            const recs = recommendationTiles.filter(tile => {
              if (tile.result.kind === 'roleScore') {
                return tile.result.value.skillOrCompetency === c.value;
              } else {
                return categoryMap[c.value].map(cs => cs.value.trainingOfferingId).includes(tile.key);
              }
            });

            categories[c.label] = [...categories[c.label], ...recs];
          }
        }

        return (
          <>
            {Object.entries(categories).map(([label, recs]) => {
              // categories.map(category => {

              /// (2) Return an empty accordion if the gap is < 100%
              if (recs.length === 0) {
                return this.renderEmptyCategory(label, label);
              }

              return this.renderRecommendationExternalCategory(label, label, recs);
            })}
          </>
        );
      }

      case 'report': {
        return (
          <>
            {this.renderReportCategory(
              i18next.t('tile-accordions.reports.client-reports', 'Reports'),
              this.props.reportTiles
            )}
          </>
        );
      }

      default:
        throw Error('Unknown TileAccordion kind');
    }
    // if (this.props.kind === "progress-participant") {
    //   return (
    //     <>
    //       {this.renderUserQuizCategory(i18next.t("tile-accordions.progress.participant.in-progress","Assessments to Complete"), this.props.quizTiles.filter((qT) => !this.userQuizIsCompleted(qT)))}
    //       {this.renderUserQuizCategory(i18next.t("tile-accordions.progress.participant.completed","Completed assessments"), this.props.quizTiles.filter((uQ) => this.userQuizIsCompleted(uQ)))}
    //     </>
    //   );
    // } else if (this.props.kind === "progress-manager") {
    //   return (
    //     <>
    //       {this.renderUserQuizCategory(i18next.t("tile-accordions.progress.manager","My Team's Assessments"), this.props.quizTiles.sort((a, b) => a.title.localeCompare(b.title)))}
    //       {/* {this.renderUserQuizCategory(i18next.t("tile-accordions.progress.completed","Completed assessments"), this.props.quizTiles.filter((uQ) => this.userQuizIsCompleted(uQ)))} */}
    //     </>
    //   );
    // } else if (this.props.kind === "recommendation-category") {
    //   const completedRecs = this.props.recommendationTiles.filter(tile => tile.completed);
    //   const notCompletedRecs = this.props.recommendationTiles.filter(tile => !tile.completed);
    //   const someCompletedRecs = completedRecs.length !== 0;
    //   return (
    //     <>
    //       {this.renderRecommendationInternalCategory(i18next.t("tile-accordions.recommendations.highly-recommended","Highly recommended"), notCompletedRecs.slice(0, 6))}
    //       {this.renderRecommendationInternalCategory(i18next.t("tile-accordions.recommendations.recommended","Recommended"), notCompletedRecs.slice(6, notCompletedRecs.length), !someCompletedRecs)}
    //       {someCompletedRecs && this.renderRecommendationInternalCategory(i18next.t("tile-accordions.recommendations.completed", "Take Again"), completedRecs)}
    //     </>
    //   )
    // } else if (this.props.kind === "report") {
    //   return (
    //     <>
    //       {this.renderReportCategory(i18next.t("tile-accordions.reports.client-reports", "Client Reports"), this.props.reportTiles)}
    //     </>
    //   )

    // } else if (this.props.kind === "recommendation-external") {
    //   const { categories, categoryMap, recommendationTiles } = this.props;
    //   return (
    //     <>
    //     {Object.keys(categoryMap).map(k => {
    //       const csRecs = recommendationTiles.filter(tile => categoryMap[k].map(cs => cs.value.trainingOfferingId).includes(tile.key));

    //       /// Don't return an accordion if there's no content
    //       if (csRecs.length === 0) { return null; }

    //       let title = k;
    //       const option = categories.find(c => c.value === k);
    //       if (option !== undefined) {
    //          title = option.label;
    //         /// Handle the problem of a mismatch between tags set in recommendations.tsx (see deprecation warning on getAssociatedCompetencyOrSkill)
    //         /// We could just overwrite with the category name, but let's see if this works first (=> consistency of data)
    //         setTags(csRecs, categoryMap, categories, option.value as string);
    //       }
    //       return this.renderRecommendationExternalCategory(title, k, csRecs);
    //     })}
    //     </>
    //   )
    // }
    // throw Error("Unknown kind");
  }

  renderEmptyCategory(title: string, index: string, openOnLoad: boolean = true): JSX.Element {
    return (
      <SectionAccordion
        key={index}
        className={styles.spacing}
        headerTitle={title}
        expandedContent={
          <div>
            {i18next.t(
              'tile-accordions.empty.content',
              'Currently there are no learning recommendations for this capability and proficiency level.'
            )}
          </div>
        }
        isOpen={openOnLoad}
      />
    );
  }

  renderRecommendationInternalCategory(
    title: string,
    recommendationTiles: RecommendationTile[],
    openOnLoad: boolean = true
  ): JSX.Element {
    return (
      <SectionAccordion
        className={styles.spacing}
        headerTitle={title}
        expandedContent={this.renderRecommendationTiles(recommendationTiles)}
        isOpen={openOnLoad}
      />
    );
  }

  renderRecommendationExternalCategory(
    title: string,
    index: string,
    recommendationTiles: RecommendationTile[],
    openOnLoad: boolean = true
  ): JSX.Element {
    return (
      <SectionAccordion
        key={index}
        className={styles.spacing}
        headerTitle={title}
        expandedContent={this.renderRecommendationTiles(recommendationTiles)}
        isOpen={openOnLoad}
      />
    );
  }

  renderUserQuizCategory(title: string, quizTiles: QuizTile[]): JSX.Element {
    return (
      <SectionAccordion
        className={styles.spacing}
        headerTitle={title}
        expandedContent={this.renderProgressTiles(quizTiles)}
      />
    );
  }

  renderReportCategory(title: string, reportTiles: ReportTile[]): JSX.Element {
    return (
      <SectionAccordion
        className={styles.spacing}
        headerTitle={title}
        expandedContent={this.renderReportTiles(reportTiles)}
      />
    );
  }

  renderProgressTiles(quizTiles: QuizTile[]): JSX.Element {
    if (quizTiles.length > 0) {
      return <div className={styles.content}>{quizTiles.map(tile => this.renderProgressTile(tile))}</div>;
    }
    return <></>;
  }

  userQuizIsCompleted(quizTile: QuizTile): boolean {
    return quizTile.progress === quizTile.total;
  }

  renderProgressTile(quizTile: QuizTile): JSX.Element {
    return (
      <div key={quizTile.id} className={styles.tileContainer}>
        <Tile
          progress={quizTile.progress}
          total={quizTile.total}
          title={quizTile.title}
          subtitle={quizTile.subtitle}
          imageUrl={quizTile.imageUrl}
          key={quizTile.id}
          kind={'user-quiz'}
          onClick={quizTile.onClick}
          enabled={quizTile.enabled}
          index={quizTile.index}
        />
      </div>
    );
  }

  renderRecommendationTiles(recommendationTiles: RecommendationTile[]): JSX.Element {
    if (recommendationTiles.length > 0) {
      return <div className={styles.content}>{recommendationTiles.map(this.renderRecommendationTile)}</div>;
    }
    return <></>;
  }

  renderRecommendationTile(recommendationTile: RecommendationTile): JSX.Element {
    return (
      <div key={recommendationTile.url} className={styles.tileContainer}>
        <Tile
          title={recommendationTile.title}
          subtitle={recommendationTile.tag}
          imageUrl={recommendationTile.imageUrl}
          key={recommendationTile.url}
          kind={'recommendation'}
          onClick={recommendationTile.onClick}
          enabled={recommendationTile.enabled}
          completed={recommendationTile.completed}
        />
      </div>
    );
  }

  renderReportTiles(reportTiles: ReportTile[]): JSX.Element {
    if (reportTiles.length > 0) {
      return <div className={styles.content}>{reportTiles.map(this.renderReportTile)}</div>;
    }
    return <></>;
  }

  renderReportTile(reportTile: ReportTile): JSX.Element {
    return (
      <div key={reportTile.id} className={styles.tileContainer}>
        <Tile
          title={reportTile.title}
          subtitle={reportTile.tag}
          imageUrl={reportTile.imageUrl}
          key={reportTile.id}
          kind={'report'}
          onClick={reportTile.onClick}
          enabled={reportTile.enabled}
        />
      </div>
    );
  }
}

// function setTags (
//   recommendations: RecommendationTile[],
//   categoryMap: StringKeyMap<string, CompetencySkillTrainingOffering[]>,
//   tags: SelectOption[],
//   categoryId: string
// ) {
//   recommendations.forEach(rec => {
//     const validId = getAssociatedCompetencyOrSkill(categoryMap, categoryId, rec.key);
//     if (validId) {
//       const displayName = tags.find(option => option.value === validId);
//       if (displayName !== undefined) {
//         rec.tag = displayName.label.toUpperCase();
//       }
//     }
//   });
// }

// /// Gets competency or skill ID from competencySkillMap, ensuring that the 'right' competency is selected
// function getAssociatedCompetencyOrSkill(
//     categoryMap: StringKeyMap<string, CompetencySkillTrainingOffering[]>,
//     categoryId: string,
//     id: string
//   ): string | undefined {
//     const mapEntry = categoryMap[categoryId];
//     if (mapEntry) {
//       const offeringEntry = categoryMap[categoryId].find(item => item.value.trainingOfferingId === id);
//       if (offeringEntry) { return categoryId; }
//     }
//     return undefined;
// }



// WEBPACK FOOTER //
// ./src/ui/components/tile-accordions/tile-accordions.tsx